import { Close } from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Box,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../store";
import { selectIsAnonymous } from "../../../store/selectors/authSelectors";
import { changeStudent, previewTeacher } from "../store/studentsSlice";

export default function TeacherInvitationPage(): React.ReactElement {
  const { token = "" } = useParams<{ token: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("texts");
  const dispatch = useAppDispatch();
  const anonymous = useAppSelector(selectIsAnonymous);
  const { preview, error, busy } = useAppSelector((state) => state.students);
  const [consent, setConsent] = useState(false);
  const [fullActivityConsent, setFullActivityConsent] = useState(false);
  useEffect((): void => {
    if (!anonymous) void dispatch(previewTeacher(token));
  }, [anonymous, dispatch, location.pathname, token]);
  if (anonymous)
    return (
      <Navigate
        to="/auth"
        state={{ from: { pathname: location.pathname } }}
        replace
      />
    );
  const join = async (): Promise<void> => {
    try {
      await dispatch(
        changeStudent({ type: "join", value: token, fullActivityConsent }),
      ).unwrap();
      navigate("/students?role=student");
    } catch {
      /* Redux displays the error. */
    }
  };
  const decline = async (): Promise<void> => {
    if (!preview?.relationshipId || preview.status !== "INVITED") return;
    try {
      await dispatch(
        changeStudent({
          type: "update",
          id: preview.relationshipId,
          action: "reject",
          invitationToken: token,
        }),
      ).unwrap();
      navigate("/students?role=student");
    } catch {
      /* Redux displays the error. */
    }
  };
  const teacherName = preview
    ? [preview.teacher.lastName, preview.teacher.firstName]
        .filter(Boolean)
        .join(" ") ||
      preview.teacher.email ||
      t("materials.teacher")
    : "";
  return (
    <Card sx={{ maxWidth: 680, mx: "auto", mt: { xs: 1, sm: 3 } }}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="h5">{t("students.invitationTitle")}</Typography>
          <IconButton
            aria-label={t("students.close")}
            onClick={(): void => {
              navigate("/students?role=student");
            }}
          >
            <Close />
          </IconButton>
        </Stack>
        {error && (
          <Alert severity="error">
            {t(`students.errors.${error}`, {
              defaultValue: t("students.errors.STUDENT_TECHNICAL_ERROR"),
            })}
          </Alert>
        )}
        {preview && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              {t("students.invitedBy", { teacher: teacherName })}
            </Typography>
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Typography>{t("students.consentTerms")}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t("students.assignmentAccess")}
              </Typography>
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={consent}
                  onChange={(event): void => setConsent(event.target.checked)}
                />
              }
              label={t("students.consentLabel")}
            />
            <Box
              sx={{
                my: 2,
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fullActivityConsent}
                    disabled={busy}
                    onChange={(event): void =>
                      setFullActivityConsent(event.target.checked)
                    }
                  />
                }
                label={t("students.fullConsentLabel")}
              />
              <Typography variant="body2" color="text.secondary">
                {t("students.fullConsentTerms")}
              </Typography>
            </Box>
            <Button
              disabled={
                busy ||
                !consent ||
                preview.status === "ACCEPTED" ||
                preview.status === "REQUESTED"
              }
              variant="contained"
              onClick={(): void => {
                void join();
              }}
            >
              {t(
                preview.relationshipId && preview.status === "INVITED"
                  ? "students.accept"
                  : "students.requestJoin",
              )}
            </Button>
            {preview.status === "INVITED" && (
              <Button
                disabled={busy}
                onClick={(): void => {
                  void decline();
                }}
              >
                {t("students.reject")}
              </Button>
            )}
            {preview.status &&
              ["ACCEPTED", "REQUESTED"].includes(preview.status) && (
                <Typography>
                  {t(`students.status.${preview.status}`)}
                </Typography>
              )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
